import { httpResource } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { AuthStore } from '@mb/auth/client';
import { isBrowser } from '@mb/common/client';
import type { OrganizationContextDto } from '../../shared';

const SELECTED_ORG_COOKIE = 'mb_selected_org';

function readPersistedOrgId(): string | undefined {
  if (!isBrowser()) return undefined;
  try {
    const entry = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${SELECTED_ORG_COOKIE}=`));
    return entry ? decodeURIComponent(entry.slice(SELECTED_ORG_COOKIE.length + 1)) : undefined;
  } catch {
    return undefined;
  }
}

export type OrganizationContextFetchParams = {
  organizationId?: string;
  purpose: 'load' | 'switch';
};

/**
 * Reactive organization context read via `httpResource`. The store owns
 * when to request (persisted default vs explicit org switch); this service
 * owns fetch lifecycle, cancellation, and loading/error signals.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationContextResourceService {
  readonly #authStore = inject(AuthStore);

  readonly #fetchParams = signal<OrganizationContextFetchParams | null>(null);

  readonly contextResource = httpResource<OrganizationContextDto>(() => {
    const params = this.#fetchParams();
    if (params === null) {
      return undefined;
    }
    if (
      !isBrowser() ||
      !this.#authStore.isInitialized() ||
      !this.#authStore.isAuthenticated()
    ) {
      return undefined;
    }

    const organizationId = params.organizationId ?? readPersistedOrgId();
    const query = organizationId
      ? `?organizationId=${encodeURIComponent(organizationId)}`
      : '';

    return {
      url: `/api/v1/organization/context${query}`,
      method: 'GET',
      withCredentials: true,
    };
  });

  readonly fetchParams = this.#fetchParams.asReadonly();

  requestDefaultLoad(): void {
    this.#fetchParams.set({ purpose: 'load' });
  }

  requestOrganization(organizationId: string): void {
    this.#fetchParams.set({ organizationId, purpose: 'switch' });
  }

  clear(): void {
    this.#fetchParams.set(null);
  }
}
